<template>
  <BaseButton
    v-if="resolved.kind === 'route'"
    :variant="variant"
    :size="size"
    as="router-link"
    :to="resolved.to"
    :data-testid="testid ?? undefined"
    @click="onClick"
  >
    <slot>{{ cta.label }}</slot>
    <template v-if="$slots['icon-right']" #icon-right>
      <slot name="icon-right" />
    </template>
  </BaseButton>
  <BaseButton
    v-else-if="resolved.kind === 'external'"
    :variant="variant"
    :size="size"
    as="a"
    :href="resolved.href"
    :data-testid="testid ?? undefined"
    @click="onClick"
  >
    <slot>{{ cta.label }}</slot>
    <template v-if="$slots['icon-right']" #icon-right>
      <slot name="icon-right" />
    </template>
  </BaseButton>
  <BaseButton
    v-else
    :variant="variant"
    :size="size"
    :data-testid="testid ?? undefined"
    @click="onClick"
  >
    <slot>{{ cta.label }}</slot>
    <template v-if="$slots['icon-right']" #icon-right>
      <slot name="icon-right" />
    </template>
  </BaseButton>
</template>

<script setup lang="ts">
/**
 * Полиморфная CTA-кнопка для hero/teaser-блоков. Сам выбирает между
 * `router-link`, `<a>` и обычной кнопкой-действием на основании
 * `resolveCtaKind()`. Сохраняет публичный API `BaseButton` (`variant`,
 * `size`, слоты), избавляя родителя от `v-if/v-else-if` разветвлений.
 */
import { computed } from 'vue';
import BaseButton from '@/components/base/BaseButton.vue';
import { resolveCtaKind, type Cta } from '@/lib/cta/resolveCtaKind';
import type { ButtonSize, ButtonVariant } from '@/components/base/BaseButton.vue';

interface Props {
  cta: Cta;
  variant?: ButtonVariant;
  size?: ButtonSize;
  testid?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'lg',
  testid: null,
});

const emit = defineEmits<{
  (e: 'action', cta: Cta): void;
}>();

const resolved = computed(() => resolveCtaKind(props.cta));

function onClick(): void {
  if (resolved.value.kind === 'action') {
    emit('action', props.cta);
  }
}
</script>
